import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { BASE_URL, fotoUrl } from '../api';
import { useIzin } from '../AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const SAAT = (s: string) => s?.slice(0, 5) ?? '';
const TR_TARIH = (s: string) =>
  new Date(s).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
const TARIH_STR = (d: Date) => d.toISOString().slice(0, 10);
const BUGUN = TARIH_STR(new Date());

const SEV_RENK: Record<string, [string, string, string]> = {
  'Başlangıç': ['#E6F1FB', 'rgba(12,68,124,0.15)',   '#0C447C'],
  'Orta':      ['#FAEEDA', 'rgba(133,79,11,0.15)',    '#854F0B'],
  'İleri':     ['#EAF3DE', 'rgba(59,109,17,0.15)',    '#3B6D11'],
};

interface Ders {
  id: string; ad: string; seviye: string; salon_adi: string;
  kapasite: number; aciklama: string; ilk_ders_tarihi: string | null;
  kayit_acik: boolean; aktif: boolean; katilim_otomatik: boolean;
  seanslar: { gun: string; baslangic_saat: string; bitis_saat: string }[];
  egitmenler: { egitmen_id: string; ad: string; soyad: string; foto_url: string | null; hakedis_oran: number }[];
}
interface Ogrenci {
  id: string; ad: string; soyad: string; email: string; tel: string;
  foto_url: string | null; kayit_tarihi: string; cinsiyet: string | null;
  kalan_hak: number; birim_fiyat: number; donduruldu: boolean;
}
interface EskiOgrenci {
  id: string; ad: string; soyad: string; email: string;
  foto_url: string | null; cinsiyet: string | null;
  kayit_tarihi: string; cikma_tarihi: string;
}
interface YoklamaOgrenci {
  id: string; ad: string; soyad: string; foto_url: string | null; cinsiyet: string | null;
  kalan_hak: number; birim_fiyat: number; onceki_durum: string | null;
}
interface YoklamaGecmis {
  id: string; tarih: string; egitmen_notu: string;
  katildi_sayisi: number; gelmedi_sayisi: number; toplam_hakedis: number;
}
interface Istatistik {
  toplam_oturum: number; toplam_katildi: number;
  toplam_gelmedi: number; toplam_hakedis: number;
}
interface TumEgitmen { id: string; ad: string; soyad: string; foto_url: string | null; }

type Sekme = 'ogrenciler' | 'yoklama_al' | 'yoklama_gecmis' | 'katilim_teyid';

export default function DersDetay() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canAtamaEgitmen = useIzin('egitmen_atama');
  const { isDark } = useTheme();

  const card      = isDark ? '#1a1a1a' : 'white';
  const cardAlt   = isDark ? '#1e1e1e' : '#fafafa';
  const border    = isDark ? '#2a2a2a' : '#eee';
  const borderSub = isDark ? '#202020' : '#f5f5f5';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec   = isDark ? '#999' : '#888';
  const textMuted = isDark ? '#666' : '#aaa';
  const barBg     = isDark ? '#333' : '#f0f0f0';

  const inputSt: React.CSSProperties = {
    width: '100%', padding: '8px 10px', borderRadius: 6,
    border: `1px solid ${isDark ? '#333' : '#ddd'}`, fontSize: 13, boxSizing: 'border-box',
    background: card, color: textPrimary,
  };
  const selectSt: React.CSSProperties = {
    padding: '8px 10px', borderRadius: 6,
    border: `1px solid ${isDark ? '#333' : '#ddd'}`, fontSize: 13, minWidth: 160,
    background: card, color: textPrimary,
  };
  const btnSt: React.CSSProperties = {
    padding: '8px 16px', borderRadius: 6,
    border: `1px solid ${isDark ? '#333' : '#ddd'}`, background: card,
    cursor: 'pointer', fontSize: 13, color: textPrimary,
  };

  const [ders, setDers] = useState<Ders | null>(null);
  const [ogrenciler, setOgrenciler] = useState<Ogrenci[]>([]);
  const [eskiOgrenciler, setEskiOgrenciler] = useState<EskiOgrenci[]>([]);
  const [yoklamalar, setYoklamalar] = useState<YoklamaGecmis[]>([]);
  const [istatistik, setIstatistik] = useState<Istatistik | null>(null);
  const [tumEgitmenler, setTumEgitmenler] = useState<TumEgitmen[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [sekme, setSekme] = useState<Sekme>('ogrenciler');
  const [acikNot, setAcikNot] = useState<string | null>(null);
  const [detayModal, setDetayModal] = useState<any>(null);
  const [yoklamaGeriAliniyor, setYoklamaGeriAliniyor] = useState(false);

  interface KrediPaket { id: string; ad: string; kredi_miktari: number; fiyat: string; }
  const [krediPaketleri, setKrediPaketleri] = useState<KrediPaket[]>([]);

  const [uzatOgrenci, setUzatOgrenci] = useState<Ogrenci | null>(null);
  const [uzatHak, setUzatHak] = useState(4);
  const [uzatTutar, setUzatTutar] = useState('');
  const [uzatHediye, setUzatHediye] = useState(false);
  const [uzatHediyeAciklama, setUzatHediyeAciklama] = useState('');
  const [uzatYukleniyor, setUzatYukleniyor] = useState(false);
  const [uzatHata, setUzatHata] = useState('');
  const [bakiyeTutar, setBakiyeTutar] = useState('');
  const [bakiyeOdemeYontemi, setBakiyeOdemeYontemi] = useState('nakit');
  const [bakiyeYukleniyor, setBakiyeYukleniyor] = useState(false);
  const [bakiyeBasarili, setBakiyeBasarili] = useState(false);
  const [uzatOgrenciBakiye, setUzatOgrenciBakiye] = useState<number | null>(null);

  const uzatAc = async (o: Ogrenci) => {
    setUzatOgrenci(o); setUzatHak(4); setUzatTutar(''); setUzatHata(''); setUzatHediye(false); setUzatHediyeAciklama('');
    setBakiyeTutar(''); setBakiyeOdemeYontemi('nakit'); setBakiyeBasarili(false);
    setUzatOgrenciBakiye(null);
    try {
      const [paketRes, bakiyeRes] = await Promise.all([
        krediPaketleri.length === 0 ? api.get('/api/krediler/paketler') : Promise.resolve({ data: krediPaketleri }),
        api.get(`/api/krediler/${o.id}`),
      ]);
      if (krediPaketleri.length === 0) setKrediPaketleri((paketRes.data || []).filter((p: any) => p.aktif));
      setUzatOgrenciBakiye(parseFloat(bakiyeRes.data?.bakiye ?? 0));
    } catch { /* sessiz */ }
  };

  const bakiyeYukle = async () => {
    if (!uzatOgrenci || !bakiyeTutar) return;
    setBakiyeYukleniyor(true);
    try {
      await api.post('/api/krediler/yukle', {
        ogrenci_id: uzatOgrenci.id,
        tutar: parseFloat(bakiyeTutar),
        odeme_yontemi: bakiyeOdemeYontemi,
      });
      setBakiyeBasarili(true);
      setBakiyeTutar('');
      setUzatOgrenciBakiye(prev => prev !== null ? prev + parseFloat(bakiyeTutar) : null);
      yukle();
    } catch (e: any) {
      alert(e?.response?.data?.hata || 'Bakiye yüklenemedi');
    } finally { setBakiyeYukleniyor(false); }
  };

  const uzatKaydet = async () => {
    if (!uzatOgrenci || (!uzatHediye && !uzatTutar)) { setUzatHata('Tutar zorunlu'); return; }
    setUzatYukleniyor(true); setUzatHata('');
    try {
      await api.post('/api/ders-hakki', {
        ogrenci_id: uzatOgrenci.id,
        ders_id: id,
        satin_alinan_hak: uzatHak,
        toplam_tutar: uzatHediye ? 0 : parseFloat(uzatTutar),
        hediye: uzatHediye,
        aciklama: uzatHediye ? (uzatHediyeAciklama || undefined) : undefined,
      });
      setUzatOgrenci(null);
      yukle();
    } catch (e: any) { setUzatHata(e.response?.data?.hata ?? 'Hata oluştu'); }
    finally { setUzatYukleniyor(false); }
  };

  interface TumDers { id: string; ad: string; seviye: string; }
  const [transferOgrenci, setTransferOgrenci] = useState<Ogrenci | null>(null);
  const [tumDersler, setTumDersler] = useState<TumDers[]>([]);
  const [transferDersId, setTransferDersId] = useState('');
  const [transferYukleniyor, setTransferYukleniyor] = useState(false);
  const [transferHata, setTransferHata] = useState('');

  const transferAc = async (o: Ogrenci) => {
    setTransferOgrenci(o); setTransferDersId(''); setTransferHata('');
    if (tumDersler.length === 0) {
      const res = await api.get('/api/dersler');
      setTumDersler(res.data.filter((d: TumDers) => d.id !== id));
    } else {
      setTumDersler(prev => prev.filter(d => d.id !== id));
    }
  };

  const transferKaydet = async () => {
    if (!transferOgrenci || !transferDersId) { setTransferHata('Ders seçilmedi'); return; }
    setTransferYukleniyor(true); setTransferHata('');
    try {
      await api.post('/api/ders-hakki/transfer', {
        ogrenci_id: transferOgrenci.id,
        eski_ders_id: id,
        yeni_ders_id: transferDersId,
      });
      setTransferOgrenci(null);
      yukle();
    } catch (e: any) { setTransferHata(e.response?.data?.hata ?? 'Hata oluştu'); }
    finally { setTransferYukleniyor(false); }
  };

  const [egitmenModal, setEgitmenModal] = useState(false);
  const [yeniEgitmenId, setYeniEgitmenId] = useState('');
  const [yeniHakedis, setYeniHakedis] = useState(0);
  const [egitmenKaydediliyor, setEgitmenKaydediliyor] = useState(false);

  const yukle = () => {
    if (!id) return;
    setYukleniyor(true);
    Promise.all([
      api.get(`/api/dersler/${id}`),
      api.get('/api/egitmenler'),
    ]).then(([r, eRes]) => {
      setDers(r.data.ders);
      setOgrenciler(r.data.ogrenciler);
      setEskiOgrenciler(r.data.eski_ogrenciler ?? []);
      setYoklamalar(r.data.yoklamalar);
      setIstatistik(r.data.istatistik);
      setTumEgitmenler(eRes.data);
    }).catch(console.error)
      .finally(() => setYukleniyor(false));
  };

  useEffect(() => { yukle(); }, [id]);

  const egitmenEkle = async () => {
    if (!yeniEgitmenId) return;
    setEgitmenKaydediliyor(true);
    try {
      await api.post(`/api/dersler/${id}/egitmenler`, { egitmen_id: yeniEgitmenId, hakedis_oran: yeniHakedis });
      setEgitmenModal(false);
      setYeniEgitmenId(''); setYeniHakedis(0);
      yukle();
    } catch (e: any) { alert(e.response?.data?.hata ?? 'Eklenemedi'); }
    finally { setEgitmenKaydediliyor(false); }
  };

  const egitmenSil = async (egitmen_id: string, ad: string) => {
    if (!window.confirm(`${ad} bu dersten çıkarılsın mı?`)) return;
    try {
      await api.delete(`/api/dersler/${id}/egitmenler/${egitmen_id}`);
      yukle();
    } catch { alert('Silinemedi'); }
  };

  if (yukleniyor) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: textMuted, fontSize: 14 }}>
      Yükleniyor...
    </div>
  );
  if (!ders) return <div style={{ padding: 24, color: textMuted }}>Ders bulunamadı.</div>;

  const [sevBgLight, sevBgDark, sevRenk] = SEV_RENK[ders.seviye] ?? ['#f0f0f0', '#333', '#666'];
  const sevBg = isDark ? sevBgDark : sevBgLight;
  const doluluk = ders.kapasite > 0 ? Math.round(ogrenciler.length / ders.kapasite * 100) : 0;
  const katilimOrani = istatistik && (Number(istatistik.toplam_katildi) + Number(istatistik.toplam_gelmedi)) > 0
    ? Math.round(Number(istatistik.toplam_katildi) / (Number(istatistik.toplam_katildi) + Number(istatistik.toplam_gelmedi)) * 100)
    : null;

  const atanamamisEgitmenler = tumEgitmenler.filter(
    e => !ders.egitmenler.find(de => de.egitmen_id === e.id)
  );

  return (
    <div style={{ padding: '24px 24px 48px' }}>

      {/* Geri */}
      <button onClick={() => navigate(-1)} style={{
        background: 'none', border: 'none', cursor: 'pointer', color: textSec, fontSize: 13,
        padding: '0 0 16px', display: 'flex', alignItems: 'center', gap: 6,
      }}>
        ← Geri
      </button>

      {/* Başlık kartı */}
      <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
              <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: textPrimary }}>{ders.ad}</h1>
              <span style={{ background: sevBg, color: sevRenk, borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 600 }}>{ders.seviye}</span>
            </div>
            <div style={{ fontSize: 13, color: textSec, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <span>🏛 {ders.salon_adi ?? 'Salon belirtilmemiş'}</span>
              <span>👥 {ogrenciler.length}/{ders.kapasite} öğrenci</span>
              {ders.seanslar.map((s, i) => (
                <span key={i}>🕐 {s.gun} {SAAT(s.baslangic_saat)}–{SAAT(s.bitis_saat)}</span>
              ))}
              {ders.ilk_ders_tarihi && (
                <span>📅 İlk ders: {new Date(ders.ilk_ders_tarihi).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              )}
            </div>
            {ders.aciklama && <div style={{ fontSize: 13, color: isDark ? '#ccc' : '#666', marginTop: 8 }}>{ders.aciklama}</div>}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            {ders.aktif === false ? (
              <span style={{ background: isDark ? '#252525' : '#f5f5f5', color: textSec, borderRadius: 20, padding: '6px 14px', fontSize: 12, fontWeight: 600, border: `1px solid ${border}` }}>
                ▣ Arşivlendi
              </span>
            ) : (
              <button
                onClick={async () => {
                  try {
                    const r = await api.patch(`/api/dersler/${ders.id}/kayit-durumu`, { kayit_acik: !ders.kayit_acik });
                    setDers(prev => prev ? { ...prev, kayit_acik: r.data.kayit_acik } : prev);
                  } catch { alert('Güncellenemedi'); }
                }}
                style={{
                  border: 'none', borderRadius: 20, padding: '6px 14px', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer',
                  background: ders.kayit_acik ? (isDark ? 'rgba(59,109,17,0.15)' : '#EAF3DE') : (isDark ? 'rgba(163,45,45,0.15)' : '#FCEBEB'),
                  color:      ders.kayit_acik ? '#3B6D11' : '#A32D2D',
                }}>
                {ders.kayit_acik ? '● Kayıt açık' : '● Kayıt kapalı'}
              </button>
            )}
            {ders.aktif !== false && (
              <button onClick={() => navigate('/dersler', { state: { duzenleId: ders.id } })}
                style={{ border: `1px solid ${border}`, background: card, borderRadius: 8, padding: '7px 16px', fontSize: 12, cursor: 'pointer', color: '#534AB7' }}>
                ✎ Düzenle
              </button>
            )}
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: textMuted, marginBottom: 4 }}>
            <span>Doluluk</span><span>%{doluluk}</span>
          </div>
          <div style={{ background: barBg, borderRadius: 4, height: 6, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 4, transition: 'width .4s',
              background: doluluk >= 90 ? '#e24b4a' : doluluk >= 70 ? '#F5A623' : '#1D9E75',
              width: `${doluluk}%`,
            }} />
          </div>
        </div>
      </div>

      {/* Eğitmenler + İstatistik */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>

        {/* Eğitmenler */}
        <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: textSec, textTransform: 'uppercase', letterSpacing: .5 }}>Eğitmenler</div>
            {canAtamaEgitmen && <button onClick={() => { setYeniEgitmenId(''); setYeniHakedis(0); setEgitmenModal(true); }}
              style={{ border: 'none', background: isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE', color: '#534AB7', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              + Ekle
            </button>}
          </div>
          {ders.egitmenler.length === 0
            ? <div style={{ fontSize: 13, color: '#e24b4a' }}>Henüz eğitmen atanmadı</div>
            : ders.egitmenler.map(e => (
              <div key={e.egitmen_id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                {e.foto_url
                  ? <img src={fotoUrl(e.foto_url)} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  : <div style={{ width: 36, height: 36, borderRadius: '50%', background: isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#534AB7', flexShrink: 0 }}>
                      {e.ad?.charAt(0)}{e.soyad?.charAt(0)}
                    </div>
                }
                <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => navigate(`/egitmenler/${e.egitmen_id}`)}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: '#534AB7' }}>{e.ad} {e.soyad}</div>
                  <div style={{ fontSize: 11, color: textSec }}>%{e.hakedis_oran} hakedis</div>
                </div>
                {canAtamaEgitmen && <button onClick={() => egitmenSil(e.egitmen_id, `${e.ad} ${e.soyad}`)}
                  style={{ border: 'none', background: 'none', color: textMuted, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '2px 4px' }}
                  title="Dersten çıkar">×</button>}
              </div>
            ))
          }
        </div>

        {/* İstatistikler */}
        <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: textSec, textTransform: 'uppercase', letterSpacing: .5, marginBottom: 14 }}>Yoklama özeti</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <StatKart baslik="Toplam oturum"  deger={String(istatistik?.toplam_oturum ?? 0)}     renk="#534AB7" bg={isDark ? 'rgba(83,74,183,0.15)' : '#EEEDFE'} />
            <StatKart baslik="Katılım oranı"  deger={katilimOrani !== null ? `%${katilimOrani}` : '—'} renk="#1D9E75" bg={isDark ? 'rgba(29,158,117,0.15)' : '#EAF3DE'} />
            <StatKart baslik="Toplam katıldı" deger={String(istatistik?.toplam_katildi ?? 0)}    renk="#3B6D11" bg={isDark ? 'rgba(59,109,17,0.15)' : '#EAF3DE'} />
            <StatKart baslik="Toplam hakedis" deger={`₺${Number(istatistik?.toplam_hakedis ?? 0).toLocaleString('tr-TR')}`} renk="#854F0B" bg={isDark ? 'rgba(133,79,11,0.15)' : '#FAEEDA'} />
          </div>
        </div>
      </div>

      {/* Sekmeler */}
      <div style={{ display: 'flex', gap: 4, background: card, borderRadius: 10, padding: 4, marginBottom: 14, border: `1px solid ${border}` }}>
        {([
          { key: 'ogrenciler',    label: 'Kayıtlı öğrenciler', badge: ogrenciler.length },
          ...( ders.aktif !== false ? [{ key: 'yoklama_al' as const, label: 'Yoklama al', badge: 0 }] : []),
          { key: 'yoklama_gecmis', label: 'Yoklama geçmişi',   badge: yoklamalar.length },
          { key: 'katilim_teyid', label: 'Katılım Teyidi',     badge: 0 },
        ] as const).map(s => (
          <button key={s.key} onClick={() => setSekme(s.key)} style={{
            flex: 1, padding: '9px 8px', borderRadius: 7, border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: sekme === s.key ? 600 : 400,
            background: sekme === s.key ? '#534AB7' : 'transparent',
            color: sekme === s.key ? 'white' : textSec,
          }}>
            {s.label}
            {s.badge > 0 && (
              <span style={{ marginLeft: 6, background: sekme === s.key ? 'rgba(255,255,255,.25)' : (isDark ? '#333' : '#eee'), borderRadius: 20, padding: '1px 7px', fontSize: 11 }}>
                {s.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Öğrenciler ── */}
      {sekme === 'ogrenciler' && (() => {
        const ogrenciCikar = async (o: Ogrenci) => {
          const hakVar = Number(o.kalan_hak ?? 0) > 0;
          const borcVar = Number(o.kalan_hak ?? 0) < 0;
          const msg = hakVar
            ? `${o.ad} ${o.soyad} bu dersten çıkarılsın mı?\n\n${o.kalan_hak} hak × ₺${Number(o.birim_fiyat).toLocaleString('tr-TR')} = ₺${(Number(o.kalan_hak) * Number(o.birim_fiyat)).toLocaleString('tr-TR')} bakiyeye iade edilecek.`
            : `${o.ad} ${o.soyad} bu dersten çıkarılsın mı?`;
          if (!window.confirm(msg)) return;

          let borcSifirla = false;
          if (borcVar) {
            const borcTutar = Math.abs(Number(o.kalan_hak) * Number(o.birim_fiyat));
            borcSifirla = window.confirm(
              `${o.ad} ${o.soyad} bu derste ${Math.abs(Number(o.kalan_hak))} hak (₺${borcTutar.toLocaleString('tr-TR')}) borçlu.\n\nBorcunu sıfırlamak (silmek) istiyor musunuz?\n\nTamam: Borç silinir\nİptal: Borç kaydı kalır`
            );
          }

          try {
            const url = `/api/dersler/${id}/ogrenciler/${o.id}${borcVar ? `?borc_sifirla=${borcSifirla}` : ''}`;
            const res = await api.delete(url);
            yukle();
            if (res.data.iade_tutar > 0) {
              alert(`✓ Öğrenci dersten çıkarıldı.\n₺${Number(res.data.iade_tutar).toLocaleString('tr-TR')} bakiyeye iade edildi.`);
            } else if (res.data.borc_sifirlandi) {
              alert(`✓ Öğrenci dersten çıkarıldı.\n₺${Number(res.data.borc_tutari).toLocaleString('tr-TR')} borç sıfırlandı.`);
            }
          } catch (e: any) { alert(e.response?.data?.hata ?? 'İşlem başarısız'); }
        };

        const aktif = ogrenciler.filter(o => Number(o.kalan_hak ?? 0) > 0);
        const pasif  = ogrenciler.filter(o => Number(o.kalan_hak ?? 0) <= 0);

        const satirRender = (o: Ogrenci, i: number, liste: Ogrenci[]) => {
          const hak = Number(o.kalan_hak ?? 0);
          const hakRenk = hak <= 0 ? '#e24b4a' : hak === 1 ? '#F5A623' : '#1D9E75';
          const hakBg   = hak <= 0 ? (isDark ? 'rgba(226,75,74,0.15)' : '#FCEBEB')
                        : hak === 1 ? (isDark ? 'rgba(245,158,11,0.15)' : '#FAEEDA')
                        : (isDark ? 'rgba(29,158,117,0.15)' : '#EAF3DE');
          const satirBg = hak <= 0
            ? (isDark ? 'rgba(163,45,45,0.08)' : '#FDECEA')
            : hak === 1
            ? (isDark ? 'rgba(133,79,11,0.08)' : '#FEF9E7')
            : o.cinsiyet === 'kadin'
            ? (isDark ? 'rgba(114,36,62,0.08)' : '#FAE0EC')
            : o.cinsiyet === 'erkek'
            ? (isDark ? 'rgba(12,68,124,0.08)' : '#DDEAF9')
            : card;
          const satirHover = hak <= 0
            ? (isDark ? 'rgba(163,45,45,0.14)' : '#FACCCA')
            : hak === 1
            ? (isDark ? 'rgba(133,79,11,0.14)' : '#FDF3CC')
            : o.cinsiyet === 'kadin'
            ? (isDark ? 'rgba(114,36,62,0.14)' : '#F5CCDF')
            : o.cinsiyet === 'erkek'
            ? (isDark ? 'rgba(12,68,124,0.14)' : '#CCDDF5')
            : (isDark ? '#252525' : '#fafafa');
          return (
            <div key={o.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '13px 18px',
                borderBottom: i < liste.length - 1 ? `1px solid ${borderSub}` : 'none',
                transition: 'background .12s', background: satirBg,
              }}
              onMouseEnter={e => (e.currentTarget.style.background = satirHover)}
              onMouseLeave={e => (e.currentTarget.style.background = satirBg)}
            >
              <div onClick={() => navigate(`/ogrenciler/${o.id}`)} style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0, cursor: 'pointer' }}>
                {o.foto_url
                  ? <img src={fotoUrl(o.foto_url)} alt="" style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  : <div style={{
                      width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                      background: o.cinsiyet === 'kadin' ? '#FBEAF0' : o.cinsiyet === 'erkek' ? '#E6F1FB' : (isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE'),
                      color:      o.cinsiyet === 'kadin' ? '#72243E' : o.cinsiyet === 'erkek' ? '#0C447C' : '#534AB7',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
                    }}>
                      {o.ad?.charAt(0)}{o.soyad?.charAt(0)}
                    </div>
                }
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: textPrimary }}>{o.ad} {o.soyad}</span>
                    {o.donduruldu && (
                      <span style={{ background: isDark ? '#252525' : '#f0f0f0', color: textSec, borderRadius: 20, padding: '1px 8px', fontSize: 10, fontWeight: 600 }}>❄ Donduruldu</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: textMuted, marginTop: 2 }}>
                    {o.email}{o.tel ? ` · ${o.tel}` : ''} · Kayıt: {TR_TARIH(o.kayit_tarihi)}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  <span style={{ background: hakBg, color: hakRenk, borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                    {hak < 0 ? `${hak} borçlu` : hak === 0 ? 'Hak yok' : `${hak} hak`}
                  </span>
                  {o.birim_fiyat > 0 && (
                    <span style={{ fontSize: 11, color: textMuted }}>₺{Number(o.birim_fiyat).toLocaleString('tr-TR')}/ders</span>
                  )}
                </div>
                <button
                  onClick={e => { e.stopPropagation(); transferAc(o); }}
                  title="Başka derse geçir"
                  style={{
                    border: `1px solid ${isDark ? 'rgba(59,91,219,0.4)' : '#c9d7f5'}`,
                    background: isDark ? 'rgba(59,91,219,0.12)' : '#f0f4ff',
                    color: '#3B5BDB', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600,
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  Değiştir
                </button>
                <button
                  onClick={e => { e.stopPropagation(); uzatAc(o); }}
                  title="Hak uzat"
                  style={{
                    border: `1px solid ${isDark ? 'rgba(29,158,117,0.4)' : '#c3dfc3'}`,
                    background: isDark ? 'rgba(29,158,117,0.12)' : '#f0f9f0',
                    color: '#1D9E75', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600,
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  Uzat
                </button>
                <button
                  onClick={e => { e.stopPropagation(); ogrenciCikar(o); }}
                  title="Dersten çıkar"
                  style={{
                    border: `1px solid ${isDark ? 'rgba(226,75,74,0.4)' : '#fcc'}`,
                    background: isDark ? 'rgba(226,75,74,0.12)' : '#fff5f5',
                    color: '#e24b4a', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600,
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  Çıkar
                </button>
              </div>
            </div>
          );
        };

        return (
          <>
            <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
              {ogrenciler.length === 0
                ? <div style={{ padding: '40px 24px', textAlign: 'center', color: textMuted, fontSize: 13 }}>Bu derse kayıtlı öğrenci yok.</div>
                : (
                  <>
                    {aktif.map((o, i) => satirRender(o, i, aktif))}
                    {pasif.length > 0 && (
                      <>
                        <div style={{ padding: '7px 18px', background: isDark ? 'rgba(226,75,74,0.08)' : '#fff5f5', borderTop: `1px solid ${isDark ? 'rgba(226,75,74,0.2)' : '#fdd'}`, borderBottom: `1px solid ${isDark ? 'rgba(226,75,74,0.2)' : '#fdd'}`, fontSize: 11, fontWeight: 600, color: '#e24b4a', letterSpacing: .3 }}>
                          HAK BİTEN ÖĞRENCİLER ({pasif.length})
                        </div>
                        {pasif.map((o, i) => satirRender(o, i, pasif))}
                      </>
                    )}
                  </>
                )
              }
            </div>

            {eskiOgrenciler.length > 0 && (
              <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ padding: '10px 18px', background: cardAlt, borderBottom: `1px solid ${border}`, fontSize: 11, fontWeight: 600, color: textSec, letterSpacing: .3 }}>
                  ESKİ ÖĞRENCİLER ({eskiOgrenciler.length})
                </div>
                {eskiOgrenciler.map((o, i) => (
                  <div key={o.id}
                    onClick={() => navigate(`/ogrenciler/${o.id}`)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14, padding: '11px 18px',
                      borderBottom: i < eskiOgrenciler.length - 1 ? `1px solid ${borderSub}` : 'none',
                      cursor: 'pointer', background: card, transition: 'background .12s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = cardAlt)}
                    onMouseLeave={e => (e.currentTarget.style.background = card)}
                  >
                    <div style={{
                      width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                      background: o.cinsiyet === 'kadin' ? '#FBEAF0' : o.cinsiyet === 'erkek' ? '#E6F1FB' : (isDark ? '#252525' : '#f0f0f0'),
                      color:      o.cinsiyet === 'kadin' ? '#72243E' : o.cinsiyet === 'erkek' ? '#0C447C' : textMuted,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, opacity: .7,
                    }}>
                      {o.ad?.charAt(0)}{o.soyad?.charAt(0)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: textSec }}>{o.ad} {o.soyad}</div>
                      <div style={{ fontSize: 11, color: textMuted, marginTop: 1 }}>{o.email}</div>
                    </div>
                    <div style={{ fontSize: 11, color: textMuted, textAlign: 'right', flexShrink: 0 }}>
                      <div>Çıkış: {TR_TARIH(o.cikma_tarihi)}</div>
                      <div style={{ marginTop: 2 }}>Kayıt: {TR_TARIH(o.kayit_tarihi)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        );
      })()}

      {/* ── Yoklama Al ── */}
      {sekme === 'yoklama_al' && id && (
        <YoklamaAlTab dersId={id} dersAdi={ders.ad} onKaydet={() => yukle()} />
      )}

      {/* ── Katılım Teyidi ── */}
      {sekme === 'katilim_teyid' && id && (
        <KatilimTeyidiTab dersId={id} seanslar={ders.seanslar} katilimOtomatik={ders.katilim_otomatik} onOtomatikDegis={yeni => setDers(prev => prev ? { ...prev, katilim_otomatik: yeni } : prev)} />
      )}

      {/* ── Yoklama Geçmişi ── */}
      {sekme === 'yoklama_gecmis' && (
        <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden' }}>
          {yoklamalar.length === 0
            ? <div style={{ padding: '40px 24px', textAlign: 'center', color: textMuted, fontSize: 13 }}>Henüz yoklama alınmamış.</div>
            : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: cardAlt, borderBottom: `1px solid ${border}` }}>
                    {['Tarih', 'Katıldı', 'Gelmedi', 'Oran', 'Hakedis'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 16px', fontSize: 11, color: textMuted, fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {yoklamalar.map((y, i) => {
                    const toplam = Number(y.katildi_sayisi) + Number(y.gelmedi_sayisi);
                    const oran = toplam > 0 ? Math.round(Number(y.katildi_sayisi) / toplam * 100) : 0;
                    return (
                      <tr key={y.id}
                        style={{ borderBottom: i < yoklamalar.length - 1 ? `1px solid ${borderSub}` : 'none', cursor: 'pointer', transition: 'background .1s' }}
                        onClick={async () => {
                          const res = await api.get(`/api/yoklama/oturum/${y.id}`);
                          setDetayModal(res.data);
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = cardAlt)}
                        onMouseLeave={e => (e.currentTarget.style.background = card)}
                      >
                        <td style={{ padding: '10px 16px', color: isDark ? '#ccc' : '#555' }}>{TR_TARIH(y.tarih)}</td>
                        <td style={{ padding: '10px 16px' }}>
                          <span style={{ background: isDark ? 'rgba(59,109,17,0.15)' : '#EAF3DE', color: '#3B6D11', borderRadius: 20, padding: '2px 8px', fontSize: 11 }}>{y.katildi_sayisi}</span>
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <span style={{ background: isDark ? 'rgba(163,45,45,0.15)' : '#FCEBEB', color: '#A32D2D', borderRadius: 20, padding: '2px 8px', fontSize: 11 }}>{y.gelmedi_sayisi}</span>
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 50, background: barBg, borderRadius: 4, height: 5, overflow: 'hidden' }}>
                              <div style={{ height: '100%', borderRadius: 4, background: oran >= 80 ? '#1D9E75' : oran >= 50 ? '#F5A623' : '#e24b4a', width: `${oran}%` }} />
                            </div>
                            <span style={{ fontSize: 11, color: textSec }}>%{oran}</span>
                          </div>
                        </td>
                        <td style={{ padding: '10px 16px', fontWeight: 500, color: '#1D9E75' }}>
                          ₺{Number(y.toplam_hakedis).toLocaleString('tr-TR')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          }
        </div>
      )}

      {/* ── Yoklama Detay Modalı ── */}
      {detayModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}
          onClick={e => { if (e.target === e.currentTarget) setDetayModal(null); }}>
          <div style={{ background: card, borderRadius: 14, width: '100%', maxWidth: 520, maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '18px 20px 14px', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: textPrimary }}>{TR_TARIH(detayModal.oturum.tarih)}</div>
                <div style={{ fontSize: 12, color: textSec, marginTop: 3 }}>{detayModal.oturum.ders_adi}</div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span style={{ background: isDark ? 'rgba(59,109,17,0.15)' : '#EAF3DE', color: '#3B6D11', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                  {detayModal.satirlar.filter((s: any) => s.durum === 'katildi').length} katıldı
                </span>
                <span style={{ background: isDark ? 'rgba(163,45,45,0.15)' : '#FCEBEB', color: '#A32D2D', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                  {detayModal.satirlar.filter((s: any) => s.durum === 'gelmedi').length} gelmedi
                </span>
                <button onClick={() => setDetayModal(null)} style={{ border: 'none', background: 'none', fontSize: 20, color: textMuted, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>×</button>
              </div>
            </div>

            <div style={{ overflowY: 'auto', flex: 1 }}>
              {detayModal.oturum.egitmen_notu && (
                <div style={{ margin: '14px 20px 0', background: isDark ? 'rgba(83,74,183,0.15)' : '#EEEDFE', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: isDark ? '#9d98e8' : '#534AB7', lineHeight: 1.6 }}>
                  <span style={{ fontWeight: 600, fontSize: 11, display: 'block', marginBottom: 4, color: '#8B7FD4' }}>DERS NOTU</span>
                  {detayModal.oturum.egitmen_notu}
                </div>
              )}

              {/* Gelir & hakedis özeti */}
              {(() => {
                const katildi = detayModal.satirlar.filter((s: any) => s.durum === 'katildi');
                const toplamGelir = katildi.reduce((sum: number, s: any) => sum + parseFloat(s.birim_fiyat_snapshot || 0), 0);
                const ucretsiz = katildi.filter((s: any) => !parseFloat(s.birim_fiyat_snapshot || 0)).length;
                const egitmenler = ders?.egitmenler?.filter(e => e.hakedis_oran > 0) ?? [];
                if (katildi.length === 0) return null;
                return (
                  <div style={{ margin: '14px 20px 0', background: isDark ? '#111' : '#f7f7ff', border: `1px solid ${isDark ? '#2a2a5a' : '#dddcf7'}`, borderRadius: 10, padding: '12px 14px' }}>
                    <div style={{ fontWeight: 600, fontSize: 11, color: isDark ? '#888' : '#888', marginBottom: 10, letterSpacing: .5 }}>OTURUM ÖZETİ</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: egitmenler.length ? 10 : 0 }}>
                      <div>
                        <span style={{ fontSize: 11, color: isDark ? '#777' : '#999' }}>Oturum geliri</span>
                        {ucretsiz > 0 && <span style={{ fontSize: 10, color: isDark ? '#555' : '#bbb', marginLeft: 6 }}>{ucretsiz} ücretsiz</span>}
                      </div>
                      <span style={{ fontSize: 16, fontWeight: 700, color: '#3C3489' }}>
                        ₺{toplamGelir.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </span>
                    </div>
                    {egitmenler.map(eg => (
                      <div key={eg.egitmen_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: `1px solid ${isDark ? '#222' : '#e8e8f5'}` }}>
                        <div>
                          <span style={{ fontSize: 12, fontWeight: 500 }}>{eg.ad} {eg.soyad}</span>
                          <span style={{ fontSize: 11, color: isDark ? '#666' : '#aaa', marginLeft: 6 }}>%{eg.hakedis_oran}</span>
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#534AB7' }}>
                          ₺{(toplamGelir * eg.hakedis_oran / 100).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {detayModal.satirlar.filter((s: any) => s.durum === 'katildi').length > 0 && (
                <div style={{ margin: '14px 20px 0' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#3B6D11', marginBottom: 8, letterSpacing: .3 }}>
                    KATILDI ({detayModal.satirlar.filter((s: any) => s.durum === 'katildi').length})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1, borderRadius: 8, overflow: 'hidden', border: `1px solid ${border}` }}>
                    {detayModal.satirlar.filter((s: any) => s.durum === 'katildi').map((s: any) => (
                      <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: isDark ? 'rgba(29,158,117,0.05)' : 'rgba(29,158,117,.04)' }}>
                        <div style={{
                          width: 30, height: 30, borderRadius: '50%', flexShrink: 0, fontSize: 11, fontWeight: 700,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: s.cinsiyet === 'kadin' ? '#FBEAF0' : s.cinsiyet === 'erkek' ? '#E6F1FB' : (isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE'),
                          color:      s.cinsiyet === 'kadin' ? '#72243E' : s.cinsiyet === 'erkek' ? '#0C447C' : '#534AB7',
                        }}>{s.ad?.charAt(0)}{s.soyad?.charAt(0)}</div>
                        <span style={{ flex: 1, fontSize: 13, color: textPrimary }}>{s.ad} {s.soyad}</span>
                        <span style={{ fontSize: 11, color: textSec }}>₺{Number(s.birim_fiyat_snapshot).toLocaleString('tr-TR')}/ders</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detayModal.satirlar.filter((s: any) => s.durum === 'gelmedi').length > 0 && (
                <div style={{ margin: '14px 20px 0' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#A32D2D', marginBottom: 8, letterSpacing: .3 }}>
                    GELMEDİ ({detayModal.satirlar.filter((s: any) => s.durum === 'gelmedi').length})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1, borderRadius: 8, overflow: 'hidden', border: `1px solid ${border}` }}>
                    {detayModal.satirlar.filter((s: any) => s.durum === 'gelmedi').map((s: any) => (
                      <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: isDark ? 'rgba(226,75,74,0.04)' : 'rgba(226,75,74,.03)' }}>
                        <div style={{
                          width: 30, height: 30, borderRadius: '50%', flexShrink: 0, fontSize: 11, fontWeight: 700,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: s.cinsiyet === 'kadin' ? '#FBEAF0' : s.cinsiyet === 'erkek' ? '#E6F1FB' : (isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE'),
                          color:      s.cinsiyet === 'kadin' ? '#72243E' : s.cinsiyet === 'erkek' ? '#0C447C' : '#534AB7',
                        }}>{s.ad?.charAt(0)}{s.soyad?.charAt(0)}</div>
                        <span style={{ flex: 1, fontSize: 13, color: textSec }}>{s.ad} {s.soyad}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ margin: '14px 20px 18px', padding: '14px 0 0', borderTop: `1px solid ${isDark ? '#222' : '#eee'}`, display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={async () => {
                    if (!detayModal?.oturum?.id) return;
                    const tarihStr = TR_TARIH(detayModal.oturum.tarih);
                    const dersAdi = detayModal.oturum.ders_adi || ders?.ad || '';
                    const katildiSayisi = detayModal.satirlar.filter((s: any) => s.durum === 'katildi').length;
                    if (!window.confirm(`"${dersAdi}" dersinin ${tarihStr} tarihli yoklaması geri alınacak.\n\n${katildiSayisi} öğrencinin ders hakkı iade edilecek ve hakedişler sıfırlanacak.\n\nEmin misiniz?`)) return;
                    setYoklamaGeriAliniyor(true);
                    try {
                      await api.delete(`/api/yoklama/oturum/${detayModal.oturum.id}`);
                      setDetayModal(null);
                      yukle();
                    } catch (err: any) {
                      alert(err.response?.data?.hata || 'Geri alınamadı');
                    } finally { setYoklamaGeriAliniyor(false); }
                  }}
                  disabled={yoklamaGeriAliniyor}
                  style={{ background: 'transparent', border: `1px solid ${isDark ? '#555' : '#ddd'}`, color: '#e24b4a', padding: '7px 16px', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600, opacity: yoklamaGeriAliniyor ? 0.6 : 1 }}
                >
                  {yoklamaGeriAliniyor ? 'Geri alınıyor...' : 'Yoklamayı Geri Al'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Eğitmen ekleme modalı ── */}
      {egitmenModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}
          onClick={e => { if (e.target === e.currentTarget) setEgitmenModal(false); }}>
          <div style={{ background: card, borderRadius: 12, padding: 24, width: '100%', maxWidth: 400 }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: textPrimary }}>Eğitmen ekle</div>
            {atanamamisEgitmenler.length === 0
              ? <div style={{ fontSize: 13, color: textMuted, marginBottom: 16 }}>Tüm eğitmenler zaten atanmış.</div>
              : <>
                <div style={{ marginBottom: 10 }}>
                  <Label isDark={isDark}>Eğitmen</Label>
                  <select value={yeniEgitmenId} onChange={e => setYeniEgitmenId(e.target.value)}
                    style={selectSt}>
                    <option value="">-- Seç --</option>
                    {atanamamisEgitmenler.map(e => (
                      <option key={e.id} value={e.id}>{e.ad} {e.soyad}</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <Label isDark={isDark}>Hakedis oranı (%)</Label>
                  <input type="number" value={yeniHakedis} min={0} max={100}
                    onChange={e => setYeniHakedis(e.target.value === '' ? 0 : Number(e.target.value))}
                    style={inputSt} />
                </div>
              </>
            }
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setEgitmenModal(false)} style={btnSt}>İptal</button>
              {atanamamisEgitmenler.length > 0 && (
                <button onClick={egitmenEkle} disabled={!yeniEgitmenId || egitmenKaydediliyor}
                  style={{ ...btnSt, background: '#534AB7', color: 'white', border: 'none', opacity: !yeniEgitmenId ? .5 : 1 }}>
                  {egitmenKaydediliyor ? 'Ekleniyor...' : 'Ekle'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Uzat Modalı ─── */}
      {uzatOgrenci && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setUzatOgrenci(null)}>
          <div style={{ background: card, borderRadius: 12, padding: 24, width: 360, boxShadow: '0 8px 32px rgba(0,0,0,.18)', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>

            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, color: textPrimary }}>
                  {uzatOgrenci.ad} {uzatOgrenci.soyad}
                </div>
                {uzatOgrenci.kalan_hak != null && (
                  <span style={{ background: uzatOgrenci.kalan_hak > 0 ? (isDark ? 'rgba(29,158,117,0.15)' : '#f0f9f0') : (isDark ? 'rgba(226,75,74,0.15)' : '#fff3f3'), color: uzatOgrenci.kalan_hak > 0 ? '#1D9E75' : '#e24b4a', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
                    {uzatOgrenci.kalan_hak > 0 ? `${uzatOgrenci.kalan_hak} hak` : uzatOgrenci.kalan_hak === 0 ? 'Hak yok' : `${Math.abs(uzatOgrenci.kalan_hak)} borçlu`}
                  </span>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: textMuted, marginBottom: 2 }}>Mevcut Bakiye</div>
                <div style={{ fontWeight: 700, fontSize: 16, color: uzatOgrenciBakiye !== null && uzatOgrenciBakiye > 0 ? '#1D9E75' : textSec }}>
                  {uzatOgrenciBakiye !== null
                    ? `₺${uzatOgrenciBakiye.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : '…'}
                </div>
              </div>
            </div>

            {/* ── Bakiye Ekle ── */}
            <div style={{ background: isDark ? 'rgba(59,91,219,0.08)' : '#f8faff', borderRadius: 8, padding: '14px 14px 12px', marginBottom: 16, border: `1px solid ${isDark ? 'rgba(59,91,219,0.2)' : '#e8edf8'}` }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#3B5BDB', marginBottom: 10 }}>💳 Bakiye Ekle</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="number" value={bakiyeTutar} onChange={e => setBakiyeTutar(e.target.value)}
                  placeholder="Tutar (₺)" style={{ ...inputSt, margin: 0, flex: 1 }}
                />
                <select value={bakiyeOdemeYontemi} onChange={e => setBakiyeOdemeYontemi(e.target.value)}
                  style={{ padding: '8px 6px', borderRadius: 6, border: `1px solid ${isDark ? '#333' : '#ddd'}`, fontSize: 12, background: card, color: textPrimary }}>
                  <option value="nakit">Nakit</option>
                  <option value="eft">EFT</option>
                  <option value="kredi_karti">Kredi Kartı</option>
                </select>
                <button onClick={bakiyeYukle} disabled={bakiyeYukleniyor || !bakiyeTutar}
                  style={{ padding: '8px 12px', borderRadius: 6, border: 'none', background: bakiyeBasarili ? '#1D9E75' : '#3B5BDB', color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {bakiyeYukleniyor ? '…' : bakiyeBasarili ? '✓ Eklendi' : 'Ekle'}
                </button>
              </div>
            </div>

            <div style={{ borderTop: `1px solid ${border}`, marginBottom: 14 }} />

            {/* ── Hak Uzat ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: textPrimary }}>📋 Ders Hakkı Ekle</div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, fontWeight: uzatHediye ? 700 : 400, color: uzatHediye ? '#f59e0b' : textSec }}>
                <input type="checkbox" checked={uzatHediye} onChange={e => { setUzatHediye(e.target.checked); setUzatTutar(''); }}
                  style={{ accentColor: '#f59e0b', width: 15, height: 15, cursor: 'pointer' }} />
                🎁 Hediye
              </label>
            </div>
            {!uzatHediye && (
              <div style={{ marginBottom: 12 }}>
                <Label isDark={isDark}>Paket Seç</Label>
                <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                  {krediPaketleri.map(p => (
                    <button key={p.id}
                      onClick={() => { setUzatHak(p.kredi_miktari); setUzatTutar(p.fiyat); }}
                      style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${uzatHak === p.kredi_miktari && uzatTutar === p.fiyat ? '#1D9E75' : (isDark ? '#333' : '#ddd')}`, background: uzatHak === p.kredi_miktari && uzatTutar === p.fiyat ? (isDark ? 'rgba(29,158,117,0.15)' : '#f0f9f0') : card, color: uzatHak === p.kredi_miktari && uzatTutar === p.fiyat ? '#1D9E75' : textSec, fontSize: 12, cursor: 'pointer', fontWeight: uzatHak === p.kredi_miktari && uzatTutar === p.fiyat ? 700 : 400 }}>
                      {p.kredi_miktari} hak — ₺{parseInt(p.fiyat).toLocaleString('tr-TR')}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <Label isDark={isDark}>Hak Sayısı</Label>
                <input type="number" min={1} value={uzatHak} onChange={e => setUzatHak(parseInt(e.target.value) || 1)} style={inputSt} />
              </div>
              {!uzatHediye && (
                <div style={{ flex: 1 }}>
                  <Label isDark={isDark}>Toplam Tutar (₺)</Label>
                  <input type="number" value={uzatTutar} onChange={e => setUzatTutar(e.target.value)}
                    placeholder={uzatOgrenci.birim_fiyat > 0 ? `≈ ₺${(uzatHak * uzatOgrenci.birim_fiyat).toLocaleString('tr-TR')}` : 'Tutar'}
                    style={inputSt} />
                </div>
              )}
              {uzatHediye && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end' }}>
                  <div style={{ background: isDark ? 'rgba(245,158,11,0.15)' : '#fef3c7', border: `1px solid ${isDark ? 'rgba(245,158,11,0.3)' : '#fcd34d'}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#92400e', fontWeight: 600, width: '100%' }}>
                    🎁 Ücretsiz eklenir
                  </div>
                </div>
              )}
            </div>
            {uzatHediye && (
              <div style={{ marginBottom: 10 }}>
                <Label isDark={isDark}>Açıklama (opsiyonel)</Label>
                <input
                  type="text"
                  value={uzatHediyeAciklama}
                  onChange={e => setUzatHediyeAciklama(e.target.value)}
                  placeholder="ör. Doğum günü hediyesi, telafi dersi..."
                  style={inputSt}
                />
              </div>
            )}
            {uzatHata && <div style={{ color: '#e24b4a', fontSize: 12, marginBottom: 8 }}>{uzatHata}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setUzatOgrenci(null)}
                style={{ flex: 1, padding: '9px 0', borderRadius: 7, border: `1px solid ${isDark ? '#333' : '#ddd'}`, background: card, cursor: 'pointer', fontSize: 13, color: textPrimary }}>
                Kapat
              </button>
              <button onClick={uzatKaydet} disabled={uzatYukleniyor || (!uzatHediye && !uzatTutar)}
                style={{ flex: 2, padding: '9px 0', borderRadius: 7, border: 'none', background: uzatHediye ? '#f59e0b' : '#1D9E75', color: 'white', cursor: (uzatYukleniyor || (!uzatHediye && !uzatTutar)) ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                {uzatYukleniyor ? 'Kaydediliyor…' : uzatHediye ? `🎁 ${uzatHak} Hak Hediye Et` : `${uzatHak} Hak Ekle`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Transfer Modalı ─── */}
      {transferOgrenci && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setTransferOgrenci(null)}>
          <div style={{ background: card, borderRadius: 12, padding: 28, width: 360, boxShadow: '0 8px 32px rgba(0,0,0,.18)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, color: textPrimary }}>Ders Değiştir</div>
            <div style={{ fontSize: 13, color: textSec, marginBottom: 18 }}>
              {transferOgrenci.ad} {transferOgrenci.soyad}
              {transferOgrenci.kalan_hak != null && (
                <span style={{ marginLeft: 8, background: transferOgrenci.kalan_hak > 0 ? (isDark ? 'rgba(59,91,219,0.15)' : '#f0f4ff') : (isDark ? '#252525' : '#f5f5f5'), color: transferOgrenci.kalan_hak > 0 ? '#3B5BDB' : textSec, borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
                  {transferOgrenci.kalan_hak > 0 ? `${transferOgrenci.kalan_hak} hak taşınacak` : 'Haksız kayıt aktarılacak'}
                </span>
              )}
            </div>
            <div style={{ marginBottom: 18 }}>
              <Label isDark={isDark}>Yeni Ders</Label>
              <select value={transferDersId} onChange={e => setTransferDersId(e.target.value)} style={{ ...inputSt, background: card }}>
                <option value="">-- Ders seçin --</option>
                {tumDersler.map(d => (
                  <option key={d.id} value={d.id}>{d.ad} {d.seviye ? `(${d.seviye})` : ''}</option>
                ))}
              </select>
            </div>
            {transferHata && <div style={{ color: '#e24b4a', fontSize: 12, marginBottom: 10 }}>{transferHata}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setTransferOgrenci(null)}
                style={{ flex: 1, padding: '9px 0', borderRadius: 7, border: `1px solid ${isDark ? '#333' : '#ddd'}`, background: card, cursor: 'pointer', fontSize: 13, color: textPrimary }}>
                İptal
              </button>
              <button onClick={transferKaydet} disabled={transferYukleniyor || !transferDersId}
                style={{ flex: 2, padding: '9px 0', borderRadius: 7, border: 'none', background: '#3B5BDB', color: 'white', cursor: (transferYukleniyor || !transferDersId) ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, opacity: !transferDersId ? .5 : 1 }}>
                {transferYukleniyor ? 'Taşınıyor…' : 'Taşı'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Yoklama Al sekmesi ────────────────────────────────────────────────────────
function YoklamaAlTab({ dersId, dersAdi, onKaydet }: {
  dersId: string;
  dersAdi: string;
  onKaydet: () => void;
}) {
  const { isDark } = useTheme();
  const card      = isDark ? '#1a1a1a' : 'white';
  const cardAlt   = isDark ? '#1e1e1e' : '#fafafa';
  const border    = isDark ? '#2a2a2a' : '#eee';
  const borderSub = isDark ? '#202020' : '#f5f5f5';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec   = isDark ? '#999' : '#888';
  const textMuted = isDark ? '#666' : '#aaa';

  const inputSt: React.CSSProperties = {
    width: '100%', padding: '8px 10px', borderRadius: 6,
    border: `1px solid ${isDark ? '#333' : '#ddd'}`, fontSize: 13, boxSizing: 'border-box',
    background: card, color: textPrimary,
  };
  const btnSt: React.CSSProperties = {
    padding: '8px 16px', borderRadius: 6,
    border: `1px solid ${isDark ? '#333' : '#ddd'}`, background: card,
    cursor: 'pointer', fontSize: 13, color: textPrimary,
  };

  const [tarih, setTarih] = useState(BUGUN);
  const [ogrenciler, setOgrenciler] = useState<YoklamaOgrenci[]>([]);
  const [durumlar, setDurumlar] = useState<Record<string, 'katildi' | 'gelmedi'>>({});
  const [not, setNot] = useState('');
  const [yoklamaAlindi, setYoklamaAlindi] = useState(false);
  const [listYukleniyor, setListYukleniyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [hata, setHata] = useState('');

  const yukle = async () => {
    setListYukleniyor(true); setHata('');
    try {
      const res = await api.get(`/api/yoklama/ders/${dersId}/tarih/${tarih}`);
      setOgrenciler(res.data.ogrenciler);
      setYoklamaAlindi(res.data.yoklama_alindi);
      setNot(res.data.egitmen_notu ?? '');
      const bas: Record<string, 'katildi' | 'gelmedi'> = {};
      res.data.ogrenciler.forEach((o: YoklamaOgrenci) => {
        const hakVar = Number(o.kalan_hak ?? 0) > 0;
        bas[o.id] = (o.onceki_durum as 'katildi' | 'gelmedi') ?? (hakVar ? 'katildi' : 'gelmedi');
      });
      setDurumlar(bas);
    } catch { setHata('Öğrenci listesi yüklenemedi.'); }
    finally { setListYukleniyor(false); }
  };

  useEffect(() => { yukle(); }, [tarih]);

  const yoklamaOgrencileri = ogrenciler;

  const hepsiniAyarla = (durum: 'katildi' | 'gelmedi') => {
    const yeni: Record<string, 'katildi' | 'gelmedi'> = {};
    yoklamaOgrencileri.forEach(o => { yeni[o.id] = durum; });
    setDurumlar(yeni);
  };

  const kaydet = async () => {
    setKaydediliyor(true); setHata('');
    try {
      const satirlar = yoklamaOgrencileri.map(o => ({
        ogrenci_id: o.id,
        durum: durumlar[o.id] ?? 'gelmedi',
        birim_fiyat_snapshot: parseFloat(String(o.birim_fiyat ?? 0)),
      }));
      const res = await api.post('/api/yoklama', { ders_id: dersId, tarih, egitmen_notu: not, satirlar });
      setYoklamaAlindi(true);
      onKaydet();
      const borcMesaj = res.data.borclu_sayisi > 0 ? `\n⚠️ ${res.data.borclu_sayisi} öğrenci için borç kaydı oluşturuldu.` : '';
      alert(`Yoklama kaydedildi. Hakedis: ₺${res.data.toplam_hakedis}${borcMesaj}`);
    } catch (e: any) { setHata(e.response?.data?.hata ?? 'Kaydedilemedi.'); }
    finally { setKaydediliyor(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, padding: '16px 18px', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div>
          <Label isDark={isDark}>Tarih</Label>
          <input type="date" value={tarih} onChange={e => setTarih(e.target.value)} max={BUGUN}
            style={{ padding: '7px 10px', borderRadius: 6, border: `1px solid ${isDark ? '#333' : '#ddd'}`, fontSize: 13, background: card, color: textPrimary }} />
        </div>
        <button onClick={() => setTarih(BUGUN)} style={{ ...btnSt, marginTop: 16 }}>Bugün</button>
        {yoklamaAlindi && (
          <span style={{ background: isDark ? 'rgba(59,109,17,0.15)' : '#EAF3DE', color: '#3B6D11', borderRadius: 20, padding: '5px 14px', fontSize: 12, fontWeight: 600, marginTop: 16 }}>✓ Bu tarih için yoklama alındı</span>
        )}
      </div>

      <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden' }}>
        {listYukleniyor ? (
          <div style={{ padding: 40, textAlign: 'center', color: textMuted, fontSize: 13 }}>Yükleniyor...</div>
        ) : ogrenciler.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: textMuted, fontSize: 13 }}>Bu tarih için aktif kayıtlı öğrenci yok.</div>
        ) : (
          <>
            <div style={{ padding: '8px 16px', background: cardAlt, borderBottom: `1px solid ${border}`, display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: textSec }}>Toplu:</span>
              <button onClick={() => hepsiniAyarla('katildi')} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #1D9E75', background: isDark ? 'rgba(29,158,117,0.1)' : 'white', color: '#1D9E75', cursor: 'pointer', fontSize: 12 }}>Hepsi katıldı</button>
              <button onClick={() => hepsiniAyarla('gelmedi')} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #e24b4a', background: isDark ? 'rgba(226,75,74,0.1)' : 'white', color: '#e24b4a', cursor: 'pointer', fontSize: 12 }}>Hepsi gelmedi</button>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: textSec }}>
                {Object.values(durumlar).filter(d => d === 'katildi').length}/{yoklamaOgrencileri.length} katıldı
              </span>
            </div>

            {yoklamaOgrencileri.map((o, i) => (
              <div key={o.id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px',
                borderBottom: i < yoklamaOgrencileri.length - 1 ? `1px solid ${borderSub}` : 'none',
                background: Number(o.kalan_hak ?? 0) <= 0
                  ? (isDark ? 'rgba(226,75,74,0.08)' : '#FFF5F5')
                  : Number(o.kalan_hak) === 1
                  ? (isDark ? 'rgba(133,79,11,0.08)' : '#FEF9E7')
                  : durumlar[o.id] === 'katildi'
                  ? (isDark ? 'rgba(29,158,117,0.05)' : 'rgba(29,158,117,.04)')
                  : card,
                transition: 'background .15s',
              }}>
                {o.foto_url
                  ? <img src={fotoUrl(o.foto_url)} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  : <div style={{
                      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                      background: o.cinsiyet === 'kadin' ? '#FBEAF0' : o.cinsiyet === 'erkek' ? '#E6F1FB' : (isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE'),
                      color:      o.cinsiyet === 'kadin' ? '#72243E' : o.cinsiyet === 'erkek' ? '#0C447C' : '#534AB7',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
                    }}>
                      {o.ad?.charAt(0)}{o.soyad?.charAt(0)}
                    </div>
                }
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: textPrimary }}>{o.ad} {o.soyad}</div>
                  <div style={{ fontSize: 11, color: Number(o.kalan_hak ?? 0) <= 0 ? '#A32D2D' : Number(o.kalan_hak) === 1 ? '#F5A623' : textMuted }}>
                    {Number(o.kalan_hak ?? 0) < 0 ? `${o.kalan_hak} borçlu` : Number(o.kalan_hak ?? 0) === 0 ? 'Hak yok' : `${o.kalan_hak} hak kaldı`}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {(['katildi', 'gelmedi'] as const).map(d => (
                    <button key={d} onClick={() => {
                      if (d === 'katildi' && Number(o.kalan_hak ?? 0) <= 0) {
                        if (!window.confirm(`${o.ad} ${o.soyad} adlı öğrencinin hakkı yok.\n\nKatıldı olarak işaretlenirse -1 borç kaydı oluşturulacak. Devam edilsin mi?`)) return;
                      }
                      setDurumlar(p => ({ ...p, [o.id]: d }));
                    }} style={{
                      padding: '5px 14px', borderRadius: 20, border: '1px solid', cursor: 'pointer', fontSize: 12, fontWeight: 500,
                      borderColor: durumlar[o.id] === d ? (d === 'katildi' ? '#1D9E75' : '#e24b4a') : (isDark ? '#333' : '#ddd'),
                      background: durumlar[o.id] === d
                        ? (d === 'katildi' ? (isDark ? 'rgba(29,158,117,0.2)' : '#EAF3DE') : (isDark ? 'rgba(226,75,74,0.2)' : '#FCEBEB'))
                        : card,
                      color: durumlar[o.id] === d ? (d === 'katildi' ? '#085041' : '#A32D2D') : textSec,
                    }}>
                      {d === 'katildi' ? '✓ Katıldı' : '✗ Gelmedi'}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div style={{ padding: '12px 16px', borderTop: `1px solid ${border}` }}>
              <Label isDark={isDark}>Eğitmen notu <span style={{ color: textMuted, fontWeight: 400 }}>(opsiyonel)</span></Label>
              <textarea value={not} onChange={e => setNot(e.target.value.slice(0, 500))}
                rows={3} placeholder="Ders notları, gözlemler..."
                style={{ ...inputSt, resize: 'vertical' as const, marginTop: 4 }} />
              <div style={{ textAlign: 'right', fontSize: 11, color: textMuted, marginTop: 2 }}>{not.length}/500</div>
            </div>
          </>
        )}

        {hata && <div style={{ margin: '0 16px 12px', background: isDark ? 'rgba(163,45,45,0.15)' : '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, fontSize: 12 }}>{hata}</div>}

        {ogrenciler.length > 0 && (
          <div style={{ padding: '12px 16px', borderTop: `1px solid ${border}`, display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={kaydet} disabled={kaydediliyor}
              style={{ padding: '9px 24px', borderRadius: 8, border: 'none', background: '#534AB7', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
              {kaydediliyor ? 'Kaydediliyor...' : yoklamaAlindi ? 'Yoklamayı güncelle' : 'Yoklamayı kaydet'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Katılım Teyidi sekmesi ───────────────────────────────────────────────────
const GUN_MAP: Record<string, number> = {
  'Pazar': 0, 'Pazartesi': 1, 'Salı': 2, 'Çarşamba': 3,
  'Perşembe': 4, 'Cuma': 5, 'Cumartesi': 6,
};

function nextClassDate(seanslar: { gun: string }[]) {
  const gunler = seanslar.map(s => GUN_MAP[s.gun]).filter((g): g is number => g !== undefined);
  if (!gunler.length) return TARIH_STR(new Date(Date.now() + 86400000));
  const base = new Date(); base.setHours(0, 0, 0, 0);
  for (let i = 1; i <= 14; i++) {
    const d = new Date(base.getTime() + i * 86400000);
    if (gunler.includes(d.getDay())) return TARIH_STR(d);
  }
  return TARIH_STR(new Date(Date.now() + 86400000));
}

function KatilimTeyidiTab({ dersId, seanslar, katilimOtomatik, onOtomatikDegis }: {
  dersId: string;
  seanslar: { gun: string; baslangic_saat: string; bitis_saat: string }[];
  katilimOtomatik: boolean;
  onOtomatikDegis: (yeni: boolean) => void;
}) {
  const { isDark } = useTheme();
  const card      = isDark ? '#1a1a1a' : 'white';
  const cardAlt   = isDark ? '#1e1e1e' : '#fafafa';
  const border    = isDark ? '#2a2a2a' : '#eee';
  const borderSub = isDark ? '#202020' : '#f5f5f5';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec   = isDark ? '#999' : '#888';
  const textMuted = isDark ? '#666' : '#aaa';
  const barBg     = isDark ? '#333' : '#f0f0f0';

  const [tarih, setTarih] = useState(() => nextClassDate(seanslar));
  const [gonderiyor, setGonderiyor] = useState(false);
  const [otomatikDegistiriyor, setOtomatikDegistiriyor] = useState(false);
  const [sonuc, setSonuc] = useState<{ eklendi: number; atlandi: number; push_gonderildi: number } | null>(null);
  const [gecmis, setGecmis] = useState<any[]>([]);
  const [gecmisYukleniyor, setGecmisYukleniyor] = useState(true);
  const [detay, setDetay] = useState<{ tarihLabel: string; liste: any[] } | null>(null);
  const [detayYukleniyor, setDetayYukleniyor] = useState(false);

  const gecmisYukle = () => {
    setGecmisYukleniyor(true);
    api.get(`/api/katilim/ders/${dersId}/gecmis`)
      .then(r => setGecmis(r.data))
      .catch(() => {})
      .finally(() => setGecmisYukleniyor(false));
  };

  useEffect(() => { gecmisYukle(); }, [dersId]);

  const detayAc = async (tarih: string) => {
    setDetay({ tarihLabel: TR_TARIH(tarih), liste: [] });
    setDetayYukleniyor(true);
    try {
      const r = await api.get(`/api/katilim/ozet/${dersId}`, { params: { tarih: tarih.slice(0, 10) } });
      setDetay({ tarihLabel: TR_TARIH(tarih), liste: r.data.liste || [] });
    } catch { /* */ }
    finally { setDetayYukleniyor(false); }
  };

  const gonder = async () => {
    if (!window.confirm(`${TR_TARIH(tarih)} tarihi için katılım teyidi gönderilsin mi?`)) return;
    setGonderiyor(true); setSonuc(null);
    try {
      const res = await api.post('/api/katilim/gonder', { ders_id: dersId, tarih });
      setSonuc({ eklendi: res.data.eklendi, atlandi: res.data.atlandi, push_gonderildi: res.data.push_gonderildi ?? 0 });
      gecmisYukle();
    } catch (e: any) {
      alert(e.response?.data?.hata ?? 'Gönderilemedi');
    } finally { setGonderiyor(false); }
  };

  const otomatikToggle = async () => {
    setOtomatikDegistiriyor(true);
    try {
      await api.patch(`/api/katilim/ders/${dersId}/otomatik`, { aktif: !katilimOtomatik });
      onOtomatikDegis(!katilimOtomatik);
    } catch { alert('Güncellenemedi'); }
    finally { setOtomatikDegistiriyor(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Otomatik gönderim */}
      <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              background: katilimOtomatik ? (isDark ? 'rgba(59,109,17,0.15)' : '#EAF3DE') : (isDark ? '#252525' : '#f5f5f5'),
              color: katilimOtomatik ? '#3B6D11' : textSec,
              borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600,
            }}>
              {katilimOtomatik ? '● Aktif' : '○ Kapalı'}
            </span>
            <span style={{ fontSize: 14, fontWeight: 600, color: textPrimary }}>Otomatik Gönderim</span>
          </div>
          <button
            onClick={otomatikToggle}
            disabled={otomatikDegistiriyor}
            style={{
              padding: '6px 14px', borderRadius: 20, border: `1px solid ${isDark ? '#333' : '#ddd'}`,
              background: katilimOtomatik ? (isDark ? 'rgba(163,45,45,0.15)' : '#FCEBEB') : (isDark ? 'rgba(59,109,17,0.15)' : '#EAF3DE'),
              color: katilimOtomatik ? '#A32D2D' : '#3B6D11',
              cursor: 'pointer', fontSize: 12, fontWeight: 600, opacity: otomatikDegistiriyor ? .6 : 1,
            }}
          >
            {katilimOtomatik ? 'Kapat' : 'Aç'}
          </button>
        </div>
        <div style={{ fontSize: 13, color: isDark ? '#ccc' : '#666', lineHeight: 1.6, marginTop: 10 }}>
          Her gün <strong>13:00'da</strong> bir sonraki ders günü için tüm kayıtlı öğrencilere katılım teyidi push bildirimi otomatik gönderilir.
        </div>
      </div>

      {/* Manuel gönderim */}
      <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, padding: '16px 18px' }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: textPrimary }}>Manuel Gönder</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <Label isDark={isDark}>Ders tarihi</Label>
            <input
              type="date"
              value={tarih}
              onChange={e => setTarih(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 6, border: `1px solid ${isDark ? '#333' : '#ddd'}`, fontSize: 13, background: card, color: textPrimary }}
            />
          </div>
          <button
            onClick={gonder}
            disabled={gonderiyor}
            style={{
              padding: '9px 20px', borderRadius: 8, border: 'none',
              background: '#534AB7', color: 'white', fontSize: 13, fontWeight: 600,
              cursor: gonderiyor ? 'wait' : 'pointer', opacity: gonderiyor ? .7 : 1,
            }}
          >
            {gonderiyor ? 'Gönderiliyor...' : '📨 Şimdi Gönder'}
          </button>
        </div>
        {sonuc && (
          <div style={{ marginTop: 12, background: isDark ? 'rgba(59,109,17,0.15)' : '#EAF3DE', color: '#3B6D11', borderRadius: 8, padding: '8px 14px', fontSize: 13 }}>
            ✓ <strong>{sonuc.eklendi}</strong> kayıt oluşturuldu, <strong>{sonuc.push_gonderildi}</strong> push gönderildi
            {sonuc.atlandi > 0 && ` (${sonuc.atlandi} zaten gönderilmişti)`}.
          </div>
        )}
      </div>

      {/* Gönderim geçmişi */}
      <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${border}`, fontSize: 13, fontWeight: 600, color: textPrimary }}>Gönderim Geçmişi</div>
        {gecmisYukleniyor ? (
          <div style={{ padding: 30, textAlign: 'center', color: textMuted, fontSize: 13 }}>Yükleniyor...</div>
        ) : gecmis.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: textMuted, fontSize: 13 }}>Henüz gönderim yapılmamış.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: cardAlt, borderBottom: `1px solid ${border}` }}>
                {['Tarih', 'Toplam', 'Evet', 'Hayır', 'Cevapsız', 'Katılım'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 16px', fontSize: 11, color: textMuted, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {gecmis.map((g, i) => {
                const evet = Number(g.evet);
                const hayir = Number(g.hayir);
                const toplam = Number(g.toplam);
                const cevapsiz = Number(g.cevapsiz);
                const cevaplayanOran = toplam > 0 ? Math.round(evet / toplam * 100) : 0;
                return (
                  <tr key={i}
                    onClick={() => detayAc(g.tarih)}
                    style={{ borderBottom: i < gecmis.length - 1 ? `1px solid ${borderSub}` : 'none', cursor: 'pointer', transition: 'background .1s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = cardAlt)}
                    onMouseLeave={e => (e.currentTarget.style.background = card)}
                  >
                    <td style={{ padding: '10px 16px', color: isDark ? '#ccc' : '#555' }}>{TR_TARIH(g.tarih)}</td>
                    <td style={{ padding: '10px 16px', color: textSec }}>{toplam}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{ background: isDark ? 'rgba(59,109,17,0.15)' : '#EAF3DE', color: '#3B6D11', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{evet}</span>
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{ background: isDark ? 'rgba(163,45,45,0.15)' : '#FCEBEB', color: '#A32D2D', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{hayir}</span>
                    </td>
                    <td style={{ padding: '10px 16px', color: textMuted }}>{cevapsiz}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 60, background: barBg, borderRadius: 4, height: 5, overflow: 'hidden' }}>
                          <div style={{ height: '100%', borderRadius: 4, width: `${cevaplayanOran}%`, background: cevaplayanOran >= 80 ? '#1D9E75' : cevaplayanOran >= 50 ? '#F5A623' : '#e24b4a' }} />
                        </div>
                        <span style={{ fontSize: 11, color: textSec }}>%{cevaplayanOran}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Detay Modalı */}
      {detay && (
        <>
          <div onClick={() => setDetay(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 200 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', background: card, borderRadius: 14, width: 440, maxHeight: '82vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', zIndex: 201, boxShadow: '0 8px 40px rgba(0,0,0,.18)' }}>
            <div style={{ padding: '18px 20px 14px', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: textPrimary }}>{detay.tarihLabel}</div>
              <button onClick={() => setDetay(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: textMuted, lineHeight: 1, padding: '0 0 0 12px' }}>×</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '14px 20px 20px' }}>
              {detayYukleniyor ? (
                <div style={{ textAlign: 'center', color: textMuted, fontSize: 13, padding: 30 }}>Yükleniyor...</div>
              ) : (() => {
                const evet     = detay.liste.filter(o => o.cevap === 'evet');
                const hayir    = detay.liste.filter(o => o.cevap === 'hayir');
                const cevapsiz = detay.liste.filter(o => o.cevap === 'cevapsiz');
                const Satir = ({ o }: { o: any }) => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 8, background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,.02)', marginBottom: 4 }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, background: o.cinsiyet === 'kadin' ? '#FBEAF0' : o.cinsiyet === 'erkek' ? '#E6F1FB' : (isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE'), color: o.cinsiyet === 'kadin' ? '#72243E' : o.cinsiyet === 'erkek' ? '#0C447C' : '#534AB7' }}>
                      {o.ad?.charAt(0)}{o.soyad?.charAt(0)}
                    </div>
                    <span style={{ flex: 1, fontSize: 13, color: textPrimary }}>{o.ad} {o.soyad}</span>
                    {o.cevap_zamani && <span style={{ fontSize: 11, color: textMuted }}>{new Date(o.cevap_zamani).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>}
                  </div>
                );
                return (
                  <>
                    {evet.length > 0 && <div style={{ marginBottom: 16 }}><div style={{ fontSize: 11, fontWeight: 700, color: '#3B6D11', marginBottom: 8, letterSpacing: .5 }}>✓ GELİYORUM ({evet.length})</div>{evet.map(o => <Satir key={o.ogrenci_id} o={o} />)}</div>}
                    {hayir.length > 0 && <div style={{ marginBottom: 16 }}><div style={{ fontSize: 11, fontWeight: 700, color: '#A32D2D', marginBottom: 8, letterSpacing: .5 }}>✗ GELEMİYORUM ({hayir.length})</div>{hayir.map(o => <Satir key={o.ogrenci_id} o={o} />)}</div>}
                    {cevapsiz.length > 0 && <div><div style={{ fontSize: 11, fontWeight: 700, color: textSec, marginBottom: 8, letterSpacing: .5 }}>? CEVAP VERMEDİ ({cevapsiz.length})</div>{cevapsiz.map(o => <Satir key={o.ogrenci_id} o={o} />)}</div>}
                  </>
                );
              })()}
            </div>
          </div>
        </>
      )}

    </div>
  );
}

// ─── Yardımcılar ──────────────────────────────────────────────────────────────
function StatKart({ baslik, deger, renk, bg }: { baslik: string; deger: string; renk: string; bg: string }) {
  return (
    <div style={{ background: bg, borderRadius: 8, padding: '10px 14px' }}>
      <div style={{ fontSize: 10, color: renk, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: .4, marginBottom: 4, opacity: .8 }}>{baslik}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: renk }}>{deger}</div>
    </div>
  );
}

function Label({ children, isDark }: { children: React.ReactNode; isDark: boolean }) {
  return <div style={{ fontSize: 11, color: isDark ? '#999' : '#888', marginBottom: 4, fontWeight: 500 }}>{children}</div>;
}
